import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, ImageOff } from "lucide-react";
import { toast } from "sonner";
import crm, { apiErr } from "../crmApi";
import { Loader } from "../CrmApp";
import { StatusBadge, inr, fileSrc } from "./WebsiteApp";

const STATUS_TABS = [["drafts", "Pending Drafts"], ["published", "Published"], ["unpublished", "Unpublished"]];

export default function WmList({ preset = "", tabs = false }) {
  const nav = useNavigate();
  const [status, setStatus] = useState(tabs ? "drafts" : preset);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    setData(null);
    crm.wm.list({ status: status || undefined, q: q || undefined }).then(setData).catch((e) => { toast.error(apiErr(e)); setData({ items: [], total: 0 }); });
  }, [status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {tabs && STATUS_TABS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`wm-status-${v}`} className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border ${status === v ? "bg-pbs-purple text-white border-pbs-purple" : "bg-white border-pbs-line text-pbs-grey"}`}>{l}</button>
        ))}
        <div className="flex items-center gap-2 border border-pbs-line bg-white rounded-lg px-3 flex-1 min-w-[200px]">
          <Search size={15} className="text-pbs-grey" />
          <input data-testid="wm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, builder, city, locality" className="flex-1 py-2 outline-none text-sm bg-transparent" />
        </div>
        <button onClick={() => nav("/crm/website/new")} data-testid="wm-add-btn" className="pbs-gradient text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1.5"><Plus size={16} /> Add Property</button>
      </div>
      {preset === "drafts" && <p className="text-xs text-pbs-grey mb-3">Listings with saved changes that are not live yet. Open one to review, preview and publish.</p>}
      {!data ? <Loader /> : (
        <div className="crm-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-pbs-grey border-b border-pbs-line">{["Listing", "Builder", "Location", "Starting Price", "Status", "Updated"].map((h) => <th key={h} className="p-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} onClick={() => nav(`/crm/website/edit/${p.id}`)} data-testid={`wm-row-${p.slug}`} className="border-b border-pbs-line/60 hover:bg-pbs-bg cursor-pointer">
                  <td className="p-3"><div className="flex items-center gap-3 min-w-[220px]">
                    {p.image ? <img src={fileSrc(p.image)} alt="" className="w-14 h-10 rounded-md object-cover bg-pbs-bg" /> : <span className="w-14 h-10 rounded-md bg-pbs-bg flex items-center justify-center text-pbs-grey"><ImageOff size={16} /></span>}
                    <div><p className="font-semibold text-pbs-dark leading-tight">{p.name}</p><p className="text-[11px] text-pbs-grey">{p.property_type} · {p.listing_type}</p></div>
                  </div></td>
                  <td className="p-3 text-pbs-grey whitespace-nowrap">{p.developer || "—"}</td>
                  <td className="p-3 text-pbs-grey whitespace-nowrap">{[p.locality, p.city].filter(Boolean).join(", ") || "—"}</td>
                  <td className="p-3 font-semibold text-pbs-dark whitespace-nowrap">{inr(p.price)}</td>
                  <td className="p-3"><StatusBadge p={p} /></td>
                  <td className="p-3 text-xs text-pbs-grey whitespace-nowrap">{p.updated_at ? new Date(p.updated_at).toLocaleDateString("en-IN") : "—"}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-pbs-grey">No listings here.</td></tr>}
            </tbody>
          </table>
          <p className="p-3 text-xs text-pbs-grey border-t border-pbs-line">{data.total} listing(s)</p>
        </div>
      )}
    </div>
  );
}
